import { ArrowRight, Award, ChevronLeft, ChevronRight, HeartHandshake, Hexagon, Leaf, Truck } from 'lucide-react';
import { Link } from 'react-router-dom';
import SectionHeader from '../../components/common/SectionHeader.jsx';
import Seo, { SITE_URL } from '../../components/common/Seo.jsx';
import CombRite from '../../components/storefront/home/CombRite.jsx';
import HeroCarousel from '../../components/storefront/home/HeroCarousel.jsx';
import ProductCarousel from '../../components/storefront/product/ProductCarousel.jsx';
import { useAppData } from '../../contexts/AppDataContext.jsx';
import { assetUrl } from '../../services/storefrontService.js';

const values = [
  {
    icon: Leaf,
    title: '100% Natural',
    text: 'Free from GMOs, glyphosate, and microplastics - nothing but what the bees intended.',
  },
  {
    icon: Award,
    title: 'Premium Since 2003',
    text: "Two decades importing the Mediterranean's finest harvests to North America.",
  },
  {
    icon: HeartHandshake,
    title: 'Direct From Beekeepers',
    text: 'Sourced from mountain apiaries in Lebanon through long-standing family partnerships.',
  },
  {
    icon: Truck,
    title: 'Shipped Within 24 Hours',
    text: 'Every jar is packed with care and on its way to your door within a day.',
  },
];

export default function HomePage() {
  const { data } = useAppData();
  const home = data.home;
  const settings = data.settings;
  const productsHref = home.categories?.[0]?.href || data.navigation?.[0]?.href || '/';

  const marqueeWords = [
    ...home.categories.map((category) => category.name.toLowerCase()),
    'raw honey',
    'royal jelly',
    'propolis',
    'bee bread',
  ];

  return (
    <>
      <Seo
        path="/"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'La Bella Trading',
          url: `${SITE_URL}/`,
          potentialAction: {
            '@type': 'SearchAction',
            target: `${SITE_URL}/search?q={search_term_string}`,
            'query-input': 'required name=search_term_string',
          },
        }}
      />
      <HeroCarousel slides={home.sliders} fallbackHref={productsHref} />

      <div className="marquee" aria-hidden="true">
        <div className="marquee-track">
          {[0, 1].map((copy) => (
            <span className="marquee-item" key={copy}>
              {marqueeWords.map((word) => (
                <span className="marquee-item" key={`${copy}-${word}`}>
                  {word} <Hexagon size={15} />
                </span>
              ))}
            </span>
          ))}
        </div>
      </div>

      <section className="container page-section">
        <div className="showcase-head">
          <div>
            <span className="eyebrow">Collections</span>
            <h2>Shop by Collection</h2>
            <p>Browse naturally sourced favorites - from medicinal honey to premium extra virgin olive oil.</p>
          </div>
          <Link className="showcase-link" to={productsHref}>All products <ArrowRight size={15} /></Link>
        </div>
        <div className="collection-grid">
          {home.categories.map((category) => (
            <Link className="collection-card" key={category.id} to={category.href}>
              <span className="collection-hex" aria-hidden="true">
                <span className="collection-hex-inner">
                  {category.photo ? <img src={assetUrl(category.photo)} alt="" loading="lazy" /> : null}
                </span>
              </span>
              <strong className="cap">{category.name.toLowerCase()}</strong>
              <span className="collection-count">{category.count} item{category.count === 1 ? '' : 's'}</span>
              <span className="collection-cta">Explore <ArrowRight size={14} /></span>
            </Link>
          ))}
        </div>
      </section>

      <section className="product-showcase">
        <div className="container">
          <div className="showcase-head">
            <div>
              <span className="eyebrow">Shop</span>
              <h2>{settings.popularProductTitle || 'Popular Products'}</h2>
              {settings.popularProductSubtitle ? <p>{settings.popularProductSubtitle}</p> : null}
            </div>
            <div className="showcase-tools">
              <Link className="showcase-link" to={productsHref}>View all products <ArrowRight size={15} /></Link>
              <div className="carousel-arrows">
                <button className="carousel-arrow carousel-prev-popular" type="button" aria-label="Previous products"><ChevronLeft size={18} /></button>
                <button className="carousel-arrow carousel-next-popular" type="button" aria-label="Next products"><ChevronRight size={18} /></button>
              </div>
            </div>
          </div>
          <ProductCarousel products={home.popularProducts} id="popular" />
        </div>
      </section>

      <CombRite ctaHref={productsHref} />

      <section className="hive-band">
        <div className="container">
          <SectionHeader
            eyebrow="Why La Bella"
            title="From the Hive, With Honor"
            text="Purity, passion, and purpose - in every drop, spoonful, and jar."
          />
          <div className="value-grid">
            {values.map((value) => (
              <div className="value-card" key={value.title}>
                <span className="hex-icon"><value.icon size={24} /></span>
                <h3>{value.title}</h3>
                <p>{value.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {home.featuredProducts.length ? (
        <section className="product-showcase product-showcase-plain">
          <div className="container">
            <div className="showcase-head">
              <div>
                <span className="eyebrow">Featured</span>
                <h2>{settings.featuredProductTitle || 'Featured Products'}</h2>
              </div>
              <div className="showcase-tools">
                <Link className="showcase-link" to={productsHref}>View all products <ArrowRight size={15} /></Link>
                <div className="carousel-arrows">
                  <button className="carousel-arrow carousel-prev-featured" type="button" aria-label="Previous products"><ChevronLeft size={18} /></button>
                  <button className="carousel-arrow carousel-next-featured" type="button" aria-label="Next products"><ChevronRight size={18} /></button>
                </div>
              </div>
            </div>
            <ProductCarousel products={home.featuredProducts} id="featured" />
          </div>
        </section>
      ) : null}
    </>
  );
}
